import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Alert,
  CircularProgress,
} from '@mui/material';
import { Send as SendIcon } from '@mui/icons-material';
import apiClient from '../services/api/apiClient';

interface NotificationTemplate {
  id: string;
  name: string;
  subject: string;
  body: string;
  type: 'job' | 'payment' | 'rating' | 'system';
  audience: 'all' | 'clients' | 'artisans';
}

const templates: NotificationTemplate[] = [
  {
    id: '1',
    name: 'New Job Request',
    subject: 'New job request available',
    body: 'A client has posted a new job request. Log in to view and accept it.',
    type: 'job',
    audience: 'artisans',
  },
  {
    id: '2',
    name: 'Payment Reminder',
    subject: 'Fund your job escrow to get started',
    body: 'Your artisan is waiting! Fund the escrow for your job to unlock work.',
    type: 'payment',
    audience: 'clients',
  },
  {
    id: '3',
    name: 'Platform Update',
    subject: 'Important update from Daily2k',
    body: 'We have made improvements to the platform. Log in to explore the latest features.',
    type: 'system',
    audience: 'all',
  },
  {
    id: '4',
    name: 'Rate Your Artisan',
    subject: 'How was your experience?',
    body: 'Your job is complete! Please leave a rating for your artisan.',
    type: 'rating',
    audience: 'clients',
  },
];

const AUDIENCE_OPTIONS = [
  { value: 'all', label: 'All Users' },
  { value: 'clients', label: 'Clients Only' },
  { value: 'artisans', label: 'Artisans Only' },
];

export default function NotificationsPage() {
  // Custom send form
  const [customSubject, setCustomSubject] = useState('');
  const [customMessage, setCustomMessage] = useState('');
  const [customAudience, setCustomAudience] = useState<'all' | 'clients' | 'artisans'>('all');
  const [customSending, setCustomSending] = useState(false);
  const [customResult, setCustomResult] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Template send dialog
  const [selectedTemplate, setSelectedTemplate] = useState<NotificationTemplate | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogSubject, setDialogSubject] = useState('');
  const [dialogMessage, setDialogMessage] = useState('');
  const [dialogAudience, setDialogAudience] = useState<'all' | 'clients' | 'artisans'>('all');
  const [dialogSending, setDialogSending] = useState(false);
  const [dialogResult, setDialogResult] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const sendBroadcast = async (
    subject: string,
    message: string,
    audience: 'all' | 'clients' | 'artisans',
  ): Promise<{ recipients_count: number }> => {
    const res = await apiClient.post('/admin/broadcast-message/', {
      subject,
      message,
      target_audience: audience,
    });
    return res.data.data;
  };

  const handleSendCustom = async () => {
    if (!customSubject.trim() || !customMessage.trim()) {
      setCustomResult({ type: 'error', text: 'Subject and message are required.' });
      return;
    }
    try {
      setCustomSending(true);
      setCustomResult(null);
      const data = await sendBroadcast(customSubject, customMessage, customAudience);
      setCustomResult({
        type: 'success',
        text: `✅ Notification sent to ${data.recipients_count} ${customAudience === 'all' ? '' : customAudience + ' '}user${data.recipients_count !== 1 ? 's' : ''}!`,
      });
      setCustomSubject('');
      setCustomMessage('');
    } catch (err: any) {
      setCustomResult({
        type: 'error',
        text: err.response?.data?.message || 'Failed to send notification.',
      });
    } finally {
      setCustomSending(false);
      setTimeout(() => setCustomResult(null), 5000);
    }
  };

  const handleOpenDialog = (template: NotificationTemplate) => {
    setSelectedTemplate(template);
    setDialogSubject(template.subject);
    setDialogMessage(template.body);
    setDialogAudience(template.audience);
    setDialogResult(null);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedTemplate(null);
    setDialogResult(null);
  };

  const handleSendTemplate = async () => {
    try {
      setDialogSending(true);
      setDialogResult(null);
      const data = await sendBroadcast(dialogSubject, dialogMessage, dialogAudience);
      setDialogResult({
        type: 'success',
        text: `✅ Sent to ${data.recipients_count} user${data.recipients_count !== 1 ? 's' : ''}!`,
      });
      setTimeout(() => { handleCloseDialog(); }, 2000);
    } catch (err: any) {
      setDialogResult({
        type: 'error',
        text: err.response?.data?.message || 'Failed to send.',
      });
    } finally {
      setDialogSending(false);
    }
  };

  const getTypeColor = (type: string): 'primary' | 'success' | 'warning' | 'info' => {
    const map: Record<string, any> = { job: 'primary', payment: 'success', rating: 'warning', system: 'info' };
    return map[type] || 'default';
  };

  return (
    <Box>
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 3 }}>
        Notifications Management
      </Typography>

      {/* ── Custom Notification ── */}
      <Card sx={{ mb: 3 }}>
        <CardHeader
          title="Send Custom Notification"
          subheader="Broadcast a message to all or specific user groups"
        />
        <CardContent>
          {customResult && (
            <Alert severity={customResult.type} sx={{ mb: 2 }}>
              {customResult.text}
            </Alert>
          )}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                fullWidth
                label="Subject"
                size="small"
                value={customSubject}
                onChange={e => setCustomSubject(e.target.value)}
              />
              <TextField
                select
                label="Audience"
                size="small"
                value={customAudience}
                onChange={e => setCustomAudience(e.target.value as any)}
                sx={{ minWidth: 160 }}
              >
                {AUDIENCE_OPTIONS.map(o => (
                  <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                ))}
              </TextField>
            </Box>
            <TextField
              fullWidth
              label="Message"
              size="small"
              multiline
              rows={3}
              value={customMessage}
              onChange={e => setCustomMessage(e.target.value)}
            />
            <Box>
              <Button
                variant="contained"
                startIcon={customSending ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
                onClick={handleSendCustom}
                disabled={customSending}
              >
                {customSending ? 'Sending...' : 'Send Notification'}
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* ── Notification Templates ── */}
      <Card>
        <CardHeader
          title="Notification Templates"
          subheader="Pre-built messages you can send with one click"
        />
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'background.paper', borderBottom: '2px solid #f3f4f6' }}>
                <TableCell sx={{ fontWeight: 700 }}>Template</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Default Audience</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Subject</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="center">Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {templates.map((template) => (
                <TableRow key={template.id} hover>
                  <TableCell sx={{ fontWeight: 600 }}>{template.name}</TableCell>
                  <TableCell>
                    <Chip label={template.type} size="small" color={getTypeColor(template.type)} />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={AUDIENCE_OPTIONS.find(o => o.value === template.audience)?.label}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell sx={{ color: 'text.secondary', fontSize: '0.85rem' }}>
                    {template.subject}
                  </TableCell>
                  <TableCell align="center">
                    <Button size="small" variant="contained" onClick={() => handleOpenDialog(template)}>
                      Send
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* ── Template Send Dialog ── */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          Send: {selectedTemplate?.name}
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {dialogResult && (
            <Alert severity={dialogResult.type} sx={{ mb: 2 }}>
              {dialogResult.text}
            </Alert>
          )}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              select
              fullWidth
              label="Send To"
              size="small"
              value={dialogAudience}
              onChange={e => setDialogAudience(e.target.value as any)}
            >
              {AUDIENCE_OPTIONS.map(o => (
                <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
              ))}
            </TextField>
            <TextField
              fullWidth
              label="Subject"
              size="small"
              value={dialogSubject}
              onChange={e => setDialogSubject(e.target.value)}
            />
            <TextField
              fullWidth
              label="Message"
              multiline
              rows={4}
              value={dialogMessage}
              onChange={e => setDialogMessage(e.target.value)}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseDialog} disabled={dialogSending}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSendTemplate}
            disabled={dialogSending}
            startIcon={dialogSending ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
          >
            {dialogSending ? 'Sending...' : 'Send Notification'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
